import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarRange, Upload, Loader2, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { W } from "@/lib/edition";
import { fmtDue } from "@/lib/subjects";

const range = (c) => (c.start === c.end ? fmtDue(c.start) : `${fmtDue(c.start)} – ${fmtDue(c.end)}`);

export const TermDatesCard = () => {
  const [cal, setCal] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/timetable").then((r) => setCal(r.data?.calendar ?? null)).catch(() => setCal(null));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/calendar/upload", fd);
      toast.success(`Term dates read: ${Object.keys(data?.weeks ?? {}).length} weeks, ${(data?.closed ?? []).length} breaks`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const remove = async () => {
    if (!window.confirm("Remove your term dates?")) return;
    await api.delete("/calendar");
    load();
  };

  return (
    <article data-testid="term-dates-card" className="min-w-0 rounded-2xl bg-surface border border-slate-200 p-5 sm:p-6 lg:col-span-2">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-orange-50 text-orange-600"><CalendarRange className="w-5 h-5" /></span>
          <div>
            <h3 className="font-semibold text-slate-900"><W s="Term dates" c="Leave & holidays" />{cal?.year ? ` ${cal.year}` : ""}</h3>
            <p className="text-sm text-slate-500"><W s="Week A/B for every week, plus holidays, half terms and INSET days. No reminders when there's no school." c="Your annual leave, bank holidays and office closures. No briefs or reminders on days off." /></p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold cursor-pointer transition-colors ${cal ? "border border-slate-200 text-slate-700 hover:bg-slate-50" : "bg-brand text-white hover:bg-brand-dark"}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{busy ? "Reading…" : cal ? "Replace" : <W s="Upload term dates" c="Upload leave dates" />}
            <input data-testid="term-dates-upload" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={busy} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
          {cal && <button data-testid="term-dates-delete" onClick={remove} aria-label="Remove term dates" className="grid place-items-center w-9 h-9 rounded-xl border border-slate-200 text-slate-500 hover:bg-rose-600 hover:text-white transition-colors"><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>
      {busy && <p className="mt-3 text-sm text-slate-500"><W s="Reading your school calendar. This takes about a minute." c="Reading your calendar. This takes about a minute." /></p>}
      {cal ? (
        <div className="mt-4">
          <p data-testid="term-dates-summary" className="text-sm text-slate-700">{cal.weeks} weeks labelled A/B. Coming up:</p>
          <ul data-testid="term-dates-upcoming" className="mt-2 grid gap-2 sm:grid-cols-2">
            {(cal.upcoming ?? []).map((c) => (
              <li key={c.start} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 border border-slate-100 px-3 py-2 text-sm">
                <span className="font-semibold text-slate-800">{c.label}</span><span className="font-mono text-xs text-slate-600">{range(c)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : cal === null && !busy && <p className="mt-3 text-sm text-slate-500"><W s="Upload a screenshot of your school's term-dates calendar." c="Upload a screenshot of your leave calendar or company holiday list." /></p>}
    </article>
  );
};
